import { useState, useCallback, useRef, useEffect } from 'react';
import threadService from '../services/threadService';

/**
 * Hook for retrying the last user message
 * Encapsulates all retry logic, state, and cleanup
 */
export const useRetryMessage = ({
  currentThread,
  isAgentCurrentlyActive,
  setFileUploadError,
  isStoppingExecution,  // Prevent RETRY during STOP
  clearStopState        // #898: el RETRY invalida el corte anterior (acción explícita del usuario)
}) => {
  const [isRetrying, setIsRetrying] = useState(false);
  const isRetryingRequestRef = useRef(false); // Prevent double-click RETRY

  /**
   * Retry the last user message by resending to N8N
   */
  const handleRetry = useCallback(async () => {
    // Cross-operation guard - block if STOP is in progress
    if (!currentThread?.id || isRetrying || isStoppingExecution) {
      if (isStoppingExecution) {
        console.log('[useRetryMessage] RETRY blocked: STOP in progress');
      }
      return;
    }

    // Agent activity guard - block if agent is already processing
    if (isAgentCurrentlyActive) {
      console.log('[useRetryMessage] RETRY blocked: agent already processing');
      return;
    }

    // Request-based debounce - block duplicate clicks during API call
    if (isRetryingRequestRef.current) {
      console.log('[useRetryMessage] RETRY blocked: request already in progress');
      return;
    }

    try {
      isRetryingRequestRef.current = true;
      setIsRetrying(true);

      // #898: el RETRY es una acción explícita del usuario y, como el mensaje
      // nuevo, invalida el corte anterior — se va el pie ⏹ y su contador de
      // pasos. Antes lo hacía por accidente la heurística «el agente se
      // reactivó» de Chat.jsx, que también se disparaba con un ping del motor
      // zombi; al retirarla, el RETRY tiene que limpiar por sí mismo o el pie
      // se quedaría clavado sobre el turno nuevo.
      // Mismo punto que useModularChat.handleRetry (ya lo hacía).
      clearStopState?.();

      console.log(`[useRetryMessage] Retrying last message for thread ${currentThread.id}`);

      const result = await threadService.retryLastMessage(currentThread.id);
      console.log('[useRetryMessage] Retry successful:', result);

      // DO NOT clear isRetrying here
      // Wait for N8N to respond and update processingStatus first
      // This prevents race condition where RETRY button re-enables before processingStatus=true
      // isRetrying will be cleared by useEffect watching processingStatus changes

    } catch (error) {
      console.error('[useRetryMessage] Error retrying:', error);
      setIsRetrying(false);

      // Show error to user
      const message = error.message || 'Error al reintentar. Intenta de nuevo.';
      setFileUploadError(message);
      setTimeout(() => setFileUploadError(null), 5000);
    } finally {
      // Always reset ref (success or error)
      isRetryingRequestRef.current = false;
    }
  }, [currentThread?.id, isRetrying, isStoppingExecution, isAgentCurrentlyActive, setFileUploadError, clearStopState]);

  // Reset RETRY debounce ref on thread change
  useEffect(() => {
    isRetryingRequestRef.current = false;
  }, [currentThread?.id]);

  // Clear isRetrying when N8N responds (processingStatus becomes active)
  // This completes the RETRY -> N8N handoff
  useEffect(() => {
    if (!currentThread?.id || !isRetrying) return;

    if (isAgentCurrentlyActive) {
      console.log('[useRetryMessage] Agent activated after RETRY, clearing isRetrying');
      setIsRetrying(false);
    }
  }, [currentThread?.id, isRetrying, isAgentCurrentlyActive]);

  return {
    handleRetry,
    isRetrying
  };
};
